import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DatePicker, Select, Input, InputNumber, Checkbox, Button, Tooltip, Upload, message, Table, Space, Switch } from 'antd';
import { UploadOutlined, DeleteOutlined, PrinterOutlined, SaveOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import QuotePreview from '../components/QuotePreview';
import { numberToWords as toWords } from '../utils/numberToWords';
import { useReactToPrint } from 'react-to-print';
import { fetchClients, addClient as apiAddClient, fetchUsers } from '../utils/api';
import { numberToWords } from '../utils/numberToWords';

const { TextArea } = Input;
const { Option } = Select;

function EditQuote({ quotation, clients = [], config = {}, user = {}, onSave, onCancel }) {
  const [clientList, setClientList] = useState(clients || []);
  const [selectedClient, setSelectedClient] = useState(quotation?.client?._id || '');
  const [billToAddress, setBillToAddress] = useState(quotation?.billToAddress || '');
  const [shipToAddress, setShipToAddress] = useState(quotation?.shipToAddress || '');
  const [clientContact, setClientContact] = useState(quotation?.clientContact || '');
  const [manualClientMode, setManualClientMode] = useState(!quotation?.client);
  const [manualClient, setManualClient] = useState({
    name: quotation?.clientName || '',
    billToAddress: quotation?.billToAddress || '',
    shipToAddress: quotation?.shipToAddress || '',
    contact: quotation?.clientContact || ''
  });
  const [reference, setReference] = useState(quotation?.reference || user?.name || '');
  const [users, setUsers] = useState([]);
  const [placeOfSupply, setPlaceOfSupply] = useState(quotation?.placeOfSupply || '');

  // Place of Supply options
  const placeOfSupplyOptions = [
    { label: 'Jammu & Kashmir (01)', value: '01' },
    { label: 'Himachal Pradesh (02)', value: '02' },
    { label: 'Punjab (03)', value: '03' },
    { label: 'Chandigarh (04)', value: '04' },
    { label: 'Uttarakhand (05)', value: '05' },
    { label: 'Haryana (06)', value: '06' },
    { label: 'Delhi (07)', value: '07' },
    { label: 'Rajasthan (08)', value: '08' },
    { label: 'Uttar Pradesh (09)', value: '09' },
    { label: 'Bihar (10)', value: '10' },
    { label: 'Sikkim (11)', value: '11' },
    { label: 'Arunachal Pradesh (12)', value: '12' },
    { label: 'Nagaland (13)', value: '13' },
    { label: 'Manipur (14)', value: '14' },
    { label: 'Mizoram (15)', value: '15' },
    { label: 'Tripura (16)', value: '16' },
    { label: 'Meghalaya (17)', value: '17' },
    { label: 'Assam (18)', value: '18' },
    { label: 'West Bengal (19)', value: '19' },
    { label: 'Jharkhand (20)', value: '20' },
    { label: 'Odisha (21)', value: '21' },
    { label: 'Chhattisgarh (22)', value: '22' },
    { label: 'Madhya Pradesh (23)', value: '23' },
    { label: 'Gujarat (24)', value: '24' },
    { label: 'Dadra and Nagar Haveli and Daman and Diu (26)', value: '26' },
    { label: 'Maharashtra (27)', value: '27' },
    { label: 'Karnataka (29)', value: '29' },
    { label: 'Goa (30)', value: '30' },
    { label: 'Lakshadweep (31)', value: '31' },
    { label: 'Kerala (32)', value: '32' },
    { label: 'Tamil Nadu (33)', value: '33' },
    { label: 'Puducherry (34)', value: '34' },
    { label: 'Andaman and Nicobar Islands (35)', value: '35' },
    { label: 'Telangana (36)', value: '36' },
    { label: 'Andhra Pradesh (37)', value: '37' },
    { label: 'Ladakh (38)', value: '38' },
  ];

  const [quoteNumber, setQuoteNumber] = useState(quotation?.quoteNumber || '');
  const [quoteDate, setQuoteDate] = useState(quotation?.quoteDate || dayjs().format('YYYY-MM-DD'));
  const [financialYear, setFinancialYear] = useState(quotation?.financialYear || '');
  const [roundOffEnabled, setRoundOffEnabled] = useState(
    quotation?.totals?.roundOff !== undefined ? Math.abs(quotation.totals.roundOff) > 0 : true
  );
  const [discountType, setDiscountType] = useState(quotation?.totals?.discountType || 'percentage');
  const [discountRate, setDiscountRate] = useState(
    quotation?.totals?.discountRate ??
    (quotation?.totals?.discountType === 'amount' ? (quotation?.totals?.discount || 0) : (quotation?.totals?.discountRate || 0))
  );
  const [saving, setSaving] = useState(false);
  const [processingImages, setProcessingImages] = useState({});
  const [items, setItems] = useState(quotation?.items || [
    { description: '', hsn: '', qty: 1, rate: 0, baseAmount: 0, taxPercent: 0, taxAmount: 0, totalAmount: 0, imageBase64: '', imageName: '' }
  ]);

  // Derive FY from date
  useEffect(() => {
    try {
      const d = new Date(quoteDate);
      if (!isNaN(d)) {
        const year = d.getFullYear();
        const nextYear = year + 1;
        setFinancialYear(`${year}-${nextYear.toString().slice(-2)}`);
      }
    } catch (e) {
      console.error('Error setting financial year:', e);
    }
  }, [quoteDate]);

  // Load clients and users
  useEffect(() => {
    const loadData = async () => {
      try {
        const [clientsData, usersData] = await Promise.all([
          fetchClients(),
          fetchUsers()
        ]);
        setClientList(clientsData);
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const subtotal = Math.round(items.reduce((sum, item) => sum + (item.baseAmount || 0), 0) * 100) / 100;
    const safeDiscountRate = Math.max(0, Number(discountRate) || 0);
    let discount = 0;
    if (discountType === 'percentage') {
      const pct = Math.min(100, safeDiscountRate);
      discount = Math.round(((subtotal * pct) / 100) * 100) / 100;
    } else {
      discount = Math.min(subtotal, Math.round(safeDiscountRate * 100) / 100);
    }

    const discountRatio = subtotal > 0 ? (discount / subtotal) : 0;
    const tax = Math.round(items.reduce((sum, item) => {
      const itemBase = Number(item.baseAmount || 0);
      const discountedItemBase = itemBase * (1 - discountRatio);
      const itemTaxPercent = Number(item.taxPercent || 0);
      return sum + (discountedItemBase * itemTaxPercent / 100);
    }, 0) * 100) / 100;

    const grossTotal = Math.round(((subtotal - discount) + tax) * 100) / 100;
    const roundedTotal = Math.round(grossTotal);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - grossTotal) * 100) / 100 : 0;
    const total = roundOffEnabled ? roundedTotal : grossTotal;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      discount: Number(discount.toFixed(2)),
      discountRate: safeDiscountRate,
      discountType,
      tax: Number(tax.toFixed(2)),
      roundOff: Number(roundOff.toFixed(2)),
      total: Number(total.toFixed(2))
    };
  }, [items, discountType, discountRate, roundOffEnabled]);

  const handleItemChange = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };

    if (field === 'qty' || field === 'rate') {
      const qty = Number(next[idx].qty || 0);
      const rate = Number(next[idx].rate || 0);
      const baseAmount = qty * rate;
      const taxPercent = Number(next[idx].taxPercent || 0);
      const taxAmount = (baseAmount * taxPercent) / 100;
      const totalAmount = baseAmount + taxAmount;

      next[idx].baseAmount = Number(baseAmount.toFixed(2));
      next[idx].taxAmount = Number(taxAmount.toFixed(2));
      next[idx].totalAmount = Number(totalAmount.toFixed(2));
    } else if (field === 'taxPercent') {
      const baseAmount = Number(next[idx].baseAmount || 0);
      const taxPercent = Number(value || 0);
      const taxAmount = (baseAmount * taxPercent) / 100;
      const totalAmount = baseAmount + taxAmount;

      next[idx].taxAmount = Number(taxAmount.toFixed(2));
      next[idx].totalAmount = Number(totalAmount.toFixed(2));
    }

    setItems(next);
  };

  const addRow = () => setItems(prev => ([...prev, { description: '', hsn: '', qty: 1, rate: 0, baseAmount: 0, taxPercent: 0, taxAmount: 0, totalAmount: 0, imageBase64: '', imageName: '' }]))
  const removeRow = (idx) => setItems(prev => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const compressImage = (file, maxWidth = 800, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, 0, 0, width, height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedBase64);
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const onImageChange = async (idx, file) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      message.error('Image file is too large. Please select an image smaller than 5MB.');
      return;
    }

    setProcessingImages(prev => ({ ...prev, [idx]: true }));

    try {
      const compressedBase64 = await compressImage(file);

      const next = [...items];
      next[idx].imageBase64 = compressedBase64;
      next[idx].imageName = file.name;
      setItems(next);
    } catch (error) {
      console.error('Error processing image:', error);
      message.error('Error processing image. Please try again.');
    } finally {
      setProcessingImages(prev => ({ ...prev, [idx]: false }));
    }
  };

  const removeImage = (idx) => {
    const next = [...items];
    next[idx].imageBase64 = '';
    next[idx].imageName = '';
    setItems(next);
  };

  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const printRef = React.useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const handleSave = async () => {
    if (!manualClientMode && !selectedClient) {
      message.error('Select client');
      return;
    }
    if (manualClientMode && !manualClient.name) {
      message.error('Enter client name');
      return;
    }
    if (!quoteNumber || !quoteDate || !financialYear) {
      message.error('Fill all required fields');
      return;
    }
    if (items.some(item => !item.description)) {
      message.error('Fill all item descriptions');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        client: manualClientMode ? null : selectedClient,
        clientName: manualClientMode ? manualClient.name : null,
        billToAddress: manualClientMode ? manualClient.billToAddress : billToAddress,
        shipToAddress: manualClientMode ? manualClient.shipToAddress : shipToAddress,
        clientContact: manualClientMode ? manualClient.contact : clientContact,
        quoteNumber,
        quoteDate,
        financialYear,
        reference,
        placeOfSupply,
        items,
        totals,
        amountInWords: toWords(totals.total)
      };

      if (onSave) {
        await onSave(payload);
      }
    } catch (e) {
      message.error(e.message || 'Failed to save quotation');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>Edit Quotation - {quotation?.quoteNumber} (v{quotation?.version})</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            loading={saving}
            icon={<SaveOutlined />}
          >
            Save Changes
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Quote Number</label>
          <Input
            value={quoteNumber}
            onChange={e => setQuoteNumber(e.target.value)}
            disabled
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Quote Date</label>
          <DatePicker
            value={dayjs(quoteDate)}
            onChange={date => setQuoteDate(date ? date.format('YYYY-MM-DD') : '')}
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Financial Year</label>
          <Input
            value={financialYear}
            onChange={e => setFinancialYear(e.target.value)}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Reference</label>
          <Input
            value={reference}
            onChange={e => setReference(e.target.value)}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Place of Supply</label>
          <Select
            value={placeOfSupply}
            onChange={setPlaceOfSupply}
            options={placeOfSupplyOptions}
            style={{ width: '100%' }}
            placeholder="Select Place of Supply"
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 5 }}>Round Off</label>
          <Checkbox
            checked={roundOffEnabled}
            onChange={e => setRoundOffEnabled(e.target.checked)}
          >
            Enable Round Off
          </Checkbox>
        </div>
      </div>

      {/* Client Selection */}
      <div style={{ marginBottom: '20px', background: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
        <div style={{ marginBottom: '15px' }}>
          <Checkbox
            checked={manualClientMode}
            onChange={e => setManualClientMode(e.target.checked)}
          >
            Manual Client Entry
          </Checkbox>
        </div>

        {manualClientMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Client Name</label>
              <Input
                value={manualClient.name}
                onChange={e => setManualClient(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Contact</label>
              <Input
                value={manualClient.contact}
                onChange={e => setManualClient(prev => ({ ...prev, contact: e.target.value }))}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Bill To Address</label>
              <TextArea
                value={manualClient.billToAddress}
                onChange={e => setManualClient(prev => ({ ...prev, billToAddress: e.target.value }))}
                rows={3}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Ship To Address</label>
              <TextArea
                value={manualClient.shipToAddress}
                onChange={e => setManualClient(prev => ({ ...prev, shipToAddress: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Select Client</label>
              <Select
                value={selectedClient}
                onChange={setSelectedClient}
                options={clientList.map(client => ({ label: client.name, value: client._id }))}
                style={{ width: '100%' }}
                placeholder="Select Client"
                showSearch
                filterOption={(input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
                }
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Contact</label>
              <Input
                value={clientContact}
                onChange={e => setClientContact(e.target.value)}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Bill To Address</label>
              <TextArea
                value={billToAddress}
                onChange={e => setBillToAddress(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Ship To Address</label>
              <TextArea
                value={shipToAddress}
                onChange={e => setShipToAddress(e.target.value)}
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Items Table */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3>Items</h3>
          <Button type="primary" onClick={addRow} icon={<PlusOutlined />}>Add Item</Button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #ddd' }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px' }}>Sl No</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', minWidth: 250 }}>Description</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 100 }}>HSN</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 80 }}>Qty</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 120 }}>Rate</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 120 }}>Base Amount</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 80 }}>GST%</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 100 }}>GST Amount</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 120 }}>Total Amount</th>
                <th style={{ border: '1px solid #f0f0f0', padding: '12px', width: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <TextArea
                        placeholder="Description"
                        value={it.description}
                        onChange={e => handleItemChange(idx, 'description', e.target.value)}
                        autoSize={{ minRows: 1, maxRows: 3 }}
                        style={{ flex: 1 }}
                      />
                      <Upload
                        showUploadList={false}
                        beforeUpload={(file) => {
                          onImageChange(idx, file);
                          return false; // prevent upload
                        }}
                        accept="image/*"
                      >
                        <Tooltip title={processingImages[idx] ? "Processing..." : "Upload Image"}>
                          <Button
                            icon={processingImages[idx] ? <UploadOutlined spin /> : <UploadOutlined />}
                            size="small"
                            style={{ borderColor: it.imageBase64 ? '#1890ff' : '#d9d9d9' }}
                          />
                        </Tooltip>
                      </Upload>
                    </div>
                    {it.imageBase64 && (
                      <div style={{ marginTop: '8px', padding: '8px', border: '1px solid #f0f0f0', borderRadius: '4px', backgroundColor: '#fafafa', position: 'relative' }}>
                        <img
                          src={it.imageBase64}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '100px',
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto'
                          }}
                        />
                        <div style={{ fontSize: '11px', color: '#888', marginTop: 4, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {it.imageName}
                        </div>
                        <Button
                          type="text"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => removeImage(idx)}
                          style={{ position: 'absolute', top: 0, right: 0, color: '#ff4d4f' }}
                        />
                      </div>
                    )}
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <Input
                      placeholder="HSN"
                      value={it.hsn}
                      onChange={e => handleItemChange(idx, 'hsn', e.target.value)}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="Qty"
                      value={it.qty}
                      onChange={v => handleItemChange(idx, 'qty', Number(v))}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="Rate"
                      value={it.rate}
                      onChange={v => handleItemChange(idx, 'rate', Number(v))}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="Amount"
                      value={it.baseAmount}
                      onChange={v => handleItemChange(idx, 'baseAmount', Number(v))}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="%"
                      value={it.taxPercent}
                      onChange={v => handleItemChange(idx, 'taxPercent', Number(v))}
                      min={0}
                      max={100}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="Tax"
                      value={it.taxAmount}
                      onChange={v => handleItemChange(idx, 'taxAmount', Number(v))}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px' }}>
                    <InputNumber
                      placeholder="Total"
                      value={it.totalAmount}
                      onChange={v => handleItemChange(idx, 'totalAmount', Number(v))}
                      min={0}
                      style={{ width: '100%' }}
                    />
                  </td>
                  <td style={{ border: '1px solid #f0f0f0', padding: '8px', textAlign: 'center' }}>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRow(idx)}
                      disabled={items.length === 1}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals */}
      <div style={{ marginBottom: '20px', background: '#f9f9f9', padding: '15px', borderRadius: '6px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Totals</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#666' }}>Round Off</span>
            <Switch size="small" checked={roundOffEnabled} onChange={setRoundOffEnabled} />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>Sub Total</label>
            <InputNumber
              value={totals.subtotal}
              readOnly
              style={{ width: '100%' }}
              prefix="₹"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>Discount</label>
            <Space.Compact style={{ width: '100%' }}>
              <Select
                value={discountType}
                onChange={setDiscountType}
                style={{ width: '45%' }}
              >
                <Select.Option value="percentage">%</Select.Option>
                <Select.Option value="amount">₹</Select.Option>
              </Select>
              <InputNumber
                value={discountRate}
                min={0}
                max={discountType === 'percentage' ? 100 : totals.subtotal}
                onChange={v => setDiscountRate(v ?? 0)}
                style={{ width: '55%' }}
                placeholder="0"
              />
            </Space.Compact>
            {totals.discount > 0 && (
              <span style={{ fontSize: 12, color: '#16a34a', display: 'block', marginTop: 4, fontWeight: 500 }}>
                -₹{totals.discount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>Tax (GST)</label>
            <InputNumber
              value={totals.tax}
              readOnly
              style={{ width: '100%' }}
              prefix="₹"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>Round Off</label>
            <InputNumber
              value={totals.roundOff}
              readOnly
              style={{ width: '100%' }}
              prefix="₹"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 5 }}>Total Amount</label>
            <InputNumber
              value={totals.total}
              readOnly
              style={{ width: '100%', fontWeight: 'bold' }}
              prefix="₹"
            />
          </div>
        </div>
        <div style={{ marginTop: '15px' }}>
          <label style={{ display: 'block', marginBottom: 5 }}>Amount in Words</label>
          <Input
            value={toWords(totals.total)}
            readOnly
            style={{ width: '100%' }}
          />
        </div>
      </div>
    </div>
  );
}

export default EditQuote;
